import {FC, useEffect, useState} from "react";

import { Box, Collapse } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import EmailInput from "@components/EmailInput";
import Icon from "@components/Icon";
import Link from "@components/Link";
import { useMessage } from "@components/Message";
import PhoneInput from "@components/PhoneInput";
import TextInput, { Textarea } from "@components/TextInput";
import { useRouter } from "@lib/router";
import { AddressBookSearchInput } from "@modules/address-book-search";
import { AddressInputs } from "@modules/address-search";
import { useAuth } from "@modules/auth";
import { DOMAIN, ROUTES } from "@utils/constants";

import { OrderFields, ORDER_MESSAGE_ZONE, ORDER_FIELDS_MAX_LENGTHS } from "../../constants";
import useRecipientForm from "../../hooks/common/useRecipientForm";
import useScrollToRecipient from "../../hooks/common/useScrollToRecipient";
import useOrderFormik from "../../hooks/useOrderFormik";
import { NewRecipient, UpdateRecipient } from "../../submodules/OrderRecipient";
import ArrivalMessage from "./ArrivalMessage";
import SectionTitle from "./SectionTitle";
import ReceiverPhoneNotificationMessage from "./ReceiverPhoneNotificationMessage";
import { findCountryCode } from "@components/PhoneInput/utils/findCountryCode";

const ReceiverAddressFormFields: FC = () => {
  const { t } = useTranslation();

  const { isLoggedIn } = useAuth();

  const { values } = useOrderFormik();

  const setMessage = useMessage();

  const destinationCountry = values[OrderFields.DESTINATION_COUNTRY];
  const isIntraEurope = values[OrderFields.IS_INTRA_EUROPE];
  const hasNewAddress = values[OrderFields.HAS_NEW_ADDRESS];

  const receiverAddressId = values[OrderFields.RECEIVER_ADDRESS_ID];

  const { defaultLocale } = useRouter();

  const [ phoneCountryCode, setPhoneCountryCode ] = useState<string | null | undefined>(null);

  const isReceiverEmailRequired = (defaultLocale === "lt" || defaultLocale === "lv") && isIntraEurope;

  const {
    isNewRecipientFormExpanded,
    isAddressSelectedFromAddressBook,
    expandNewRecipientForm,
    handleAddressBookItemSelected,
    handleNewRecipientExpandChanged,
  } = useRecipientForm();

  const handleAddressSearchError = () => {
    setMessage({
      message: t("order-message-failed_address_search"),
      props: {
        variant: "error",
        icon: <Icon icon="warning" size="1.45rem" />,
        canClose: true,
        padding: "0.45rem 0.75rem",
      },
      zone: ORDER_MESSAGE_ZONE,
    });
  };

  useEffect(() => {
    const phoneNumber = values[OrderFields.RECEIVER_PHONE_NUMBER];
    const phoneNumberCode = phoneNumber ? findCountryCode(phoneNumber) : null;
    if (phoneNumberCode) {
      setPhoneCountryCode(phoneNumberCode);
    }
  }, [values[OrderFields.RECEIVER_PHONE_NUMBER]])

  useEffect(() => {
    if (hasNewAddress) {
      expandNewRecipientForm();
    }
  }, [expandNewRecipientForm, hasNewAddress]);

  const recipientRef = useScrollToRecipient();

  const isNewRecipientFormVisible = isLoggedIn && (!receiverAddressId || hasNewAddress);
  const isUpdateRecipientFormVisible = isLoggedIn && !!receiverAddressId && !hasNewAddress;

  return (
    <FullWidthVStack spacing="1rem" ref={recipientRef}>
      <FullWidthVStack spacing="1rem">
        <SectionTitle>{t("order-receiver-receiver_details")}</SectionTitle>
        {isLoggedIn && (
          <AddressBookSearchInput
            id="order-receiver-address-address_book_search"
            type="address"
            countryCode={destinationCountry!}
            onAddressSelect={handleAddressBookItemSelected}
          />
        )}
      </FullWidthVStack>
      <Collapse in={!isLoggedIn || !isNewRecipientFormExpanded}>
        <FullWidthVStack spacing="1rem" pb="0.75rem">
          <Box data-field={OrderFields.RECEIVER_NAME}>
            <TextInput
              name={OrderFields.RECEIVER_NAME}
              label={`${t("order-receiver-name")}`}
              id="order-receiver-name"
              maxLength={ORDER_FIELDS_MAX_LENGTHS.name}
            />
          </Box>
          <Box data-field={OrderFields.RECEIVER_PHONE_NUMBER}>
            <PhoneInput
              name={OrderFields.RECEIVER_PHONE_NUMBER}
              label={t("order-receiver-mobile_number")}
              id="order-receiver-phone"
              onPhoneCodeChange={setPhoneCountryCode}
              pinnedPhoneCodes={DOMAIN.DOMAIN_PHONE_NUMBERS[defaultLocale ?? ""]}
              defaultCountry={values[OrderFields.DESTINATION_COUNTRY]!}
            />
          </Box>
          <ReceiverPhoneNotificationMessage phoneCountryCode={phoneCountryCode} />
          <Box data-field={OrderFields.RECEIVER_EMAIL}>
            <EmailInput
              name={OrderFields.RECEIVER_EMAIL}
              label={`${t("order-receiver-email")} ${!isReceiverEmailRequired ? t("common-optional") : ""}`}
              id="order-receiver-email"
              maxLength={ORDER_FIELDS_MAX_LENGTHS.email}
            />
          </Box>
        </FullWidthVStack>
        <FullWidthVStack spacing="1rem">
          <SectionTitle>{t("order-receiver-address_details")}</SectionTitle>
          <Box data-field={OrderFields.DESTINATION_COUNTRY_NAME}>
            <TextInput
              name={OrderFields.DESTINATION_COUNTRY_NAME}
              isReadOnly
              rightAddon={<Link label={t("order-field-edit")} href={ROUTES.ORDER} id="order-receiver-country_edit" />}
              id="order-receiver-country"
            />
          </Box>
          <AddressInputs
            id="order-receiver-address"
            countryCode={destinationCountry!}
            streetFieldName={OrderFields.RECEIVER_STREET}
            houseNoFieldName={OrderFields.RECEIVER_HOUSE_NO}
            flatNoFieldName={OrderFields.RECEIVER_FLAT_NO}
            cityFieldName={OrderFields.RECEIVER_CITY}
            zipCodeFieldName={OrderFields.RECEIVER_ZIP_CODE}
            isAddressSelectedFromAddressBook={isAddressSelectedFromAddressBook}
            onError={handleAddressSearchError}
          />
          <Box data-field={OrderFields.RECEIVER_COMMENTS}>
            <Textarea
              name={OrderFields.RECEIVER_COMMENTS}
              label={`${t("order-receiver-comments_for_courier")} ${t("common-optional")}`}
              id="order-receiver-courier_comments"
              maxLength={ORDER_FIELDS_MAX_LENGTHS.comment}
            />
          </Box>
        </FullWidthVStack>
      </Collapse>
      {isNewRecipientFormVisible && (
        <NewRecipient
          isExpanded={isNewRecipientFormExpanded}
          country={destinationCountry!}
          onExpandChange={handleNewRecipientExpandChanged}
          isLockerForm={false}
        />
      )}
      {isUpdateRecipientFormVisible && <UpdateRecipient />}
      <ArrivalMessage />
    </FullWidthVStack>
  );
};
export default ReceiverAddressFormFields;
